import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#5a6268'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #f8f9fa;
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.85rem;
  color: #555;
  border-bottom: 1px solid #eee;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;
  color: #333;
  vertical-align: middle;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  background: ${props => (props.active ? '#d4edda' : '#f8d7da')};
  color: ${props => (props.active ? '#155724' : '#721c24')};
`;

const Modal = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const Hint = styled.p`
  font-size: 0.78rem;
  color: #888;
  margin: 0.35rem 0 0;
`;

const ErrorText = styled.p`
  color: #dc3545;
  font-size: 0.85rem;
  margin: 0 0 1rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Empty = styled.div`
  padding: 3rem;
  text-align: center;
  color: #888;
  background: white;
  border-radius: 8px;
`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
});

const emptyForm = {
  username: '',
  password: '',
  companyName: '',
  contactName: '',
  contactPhone: '',
  defaultCommissionRate: ''
};

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null=新建
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/api/admin/suppliers', authHeaders());
      setSuppliers(res.data.suppliers || []);
    } catch (err) {
      const status = err.response && err.response.status;
      setError(status === 403 ? '当前账号权限不足' : '获取供应商列表失败');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (supplier) => {
    setEditing(supplier);
    setForm({
      username: supplier.username || '',
      password: '',
      companyName: supplier.supplierInfo?.companyName || '',
      contactName: supplier.supplierInfo?.contactName || '',
      contactPhone: supplier.supplierInfo?.contactPhone || '',
      defaultCommissionRate: supplier.supplierInfo?.defaultCommissionRate ?? ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!editing) {
        await axios.post('/api/admin/suppliers', form, authHeaders());
        showToast('供应商账号创建成功');
      } else {
        const payload = {
          companyName: form.companyName,
          contactName: form.contactName,
          contactPhone: form.contactPhone,
          defaultCommissionRate: form.defaultCommissionRate === '' ? 0 : Number(form.defaultCommissionRate)
        };
        if (form.password) payload.password = form.password;
        await axios.put(`/api/admin/suppliers/${editing._id}`, payload, authHeaders());
        showToast('供应商账号已更新');
      }

      setShowModal(false);
      fetchSuppliers();
    } catch (err) {
      const msg = err.response?.data?.message || '保存失败';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (supplier) => {
    try {
      await axios.put(
        `/api/admin/suppliers/${supplier._id}`,
        { isActive: !supplier.isActive },
        authHeaders()
      );
      showToast(supplier.isActive ? '已停用该供应商' : '已启用该供应商');
      fetchSuppliers();
    } catch (err) {
      showToast(err.response?.data?.message || '操作失败', 'error');
    }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`确定删除供应商「${supplier.supplierInfo?.companyName || supplier.username}」吗？`)) return;
    try {
      await axios.delete(`/api/admin/suppliers/${supplier._id}`, authHeaders());
      showToast('供应商已删除');
      fetchSuppliers();
    } catch (err) {
      showToast(err.response?.data?.message || '删除失败', 'error');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  return (
    <AdminLayout title="供应商管理">
      <Container>
        <Header>
          <Title>供应商管理</Title>
          <Button primary onClick={openCreate}>+ 新增供应商</Button>
        </Header>

        {toast && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            background: toast.type === 'error' ? '#f8d7da' : '#d4edda',
            color: toast.type === 'error' ? '#721c24' : '#155724',
            fontSize: '0.9rem'
          }}>
            {toast.message}
          </div>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        {loading ? (
          <Empty>加载中...</Empty>
        ) : suppliers.length === 0 ? (
          <Empty>暂无供应商账号，点击右上角「新增供应商」创建</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>登录账号</Th>
                <Th>公司名称</Th>
                <Th>联系人</Th>
                <Th>联系电话</Th>
                <Th>默认分成比</Th>
                <Th>产品数</Th>
                <Th>状态</Th>
                <Th>操作</Th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s._id}>
                  <Td>{s.username}</Td>
                  <Td>{s.supplierInfo?.companyName || '-'}</Td>
                  <Td>{s.supplierInfo?.contactName || '-'}</Td>
                  <Td>{s.supplierInfo?.contactPhone || '-'}</Td>
                  <Td>{s.supplierInfo?.defaultCommissionRate ?? 0}%</Td>
                  <Td>{s.productTotal || 0}</Td>
                  <Td>
                    <StatusBadge active={s.isActive}>{s.isActive ? '启用' : '停用'}</StatusBadge>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button onClick={() => openEdit(s)}>编辑</Button>
                      <Button success={!s.isActive} danger={s.isActive} onClick={() => toggleActive(s)}>
                        {s.isActive ? '停用' : '启用'}
                      </Button>
                      <Button danger onClick={() => handleDelete(s)}>删除</Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <Title style={{ fontSize: '1.15rem', marginBottom: '1.25rem' }}>
                {editing ? '编辑供应商' : '新增供应商'}
              </Title>

              {error && <ErrorText>{error}</ErrorText>}

              <FormGroup>
                <Label>登录账号</Label>
                <Input
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  disabled={!!editing}
                  placeholder="供应商登录后台使用的账号"
                />
              </FormGroup>

              <FormGroup>
                <Label>{editing ? '重置密码（留空则不修改）' : '登录密码'}</Label>
                <Input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="至少6位"
                />
              </FormGroup>

              <FormGroup>
                <Label>公司名称</Label>
                <Input name="companyName" value={form.companyName} onChange={handleChange} />
              </FormGroup>

              <FormGroup>
                <Label>联系人</Label>
                <Input name="contactName" value={form.contactName} onChange={handleChange} />
              </FormGroup>

              <FormGroup>
                <Label>联系电话</Label>
                <Input name="contactPhone" value={form.contactPhone} onChange={handleChange} />
              </FormGroup>

              <FormGroup>
                <Label>默认分成比（给我司，%）</Label>
                <Input
                  name="defaultCommissionRate"
                  type="number"
                  min="0"
                  max="100"
                  value={form.defaultCommissionRate}
                  onChange={handleChange}
                  placeholder="如 15 表示销售额的15%归我司"
                />
                <Hint>该值作为供应商新建产品时分成比的默认值，具体产品可单独调整</Hint>
              </FormGroup>

              <ModalActions>
                <Button onClick={() => setShowModal(false)}>取消</Button>
                <Button primary onClick={handleSave} disabled={saving}>
                  {saving ? '保存中...' : '保存'}
                </Button>
              </ModalActions>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default SuppliersPage;
